import { Controller } from '@hotwired/stimulus';
import { application } from 'src';
import { HourDifference, isBlank, isPresent, rJust, min, advanceDate, hoursToMinutes } from '@fretadao/f-js-utils';
import { find, findAll } from '@fretadao/f-js-dom';

application.register(
  'line-schedule-requests-form-calculate-shift-time',
  class extends Controller {
    static targets = ['timeShiftType', 'fixedTime', 'shiftDelta'];

    connect() {
      document.addEventListener('f-components-table:loaded', this.#calculateDiffOnTableLoading.bind(this));
    }

    disconnect() {
      document.removeEventListener('f-components-table:loaded', this.#calculateDiffOnTableLoading.bind(this));
    }

    calculateDiffOnFieldChanges() {
      if (this.#withoutTimeFilled()) {
        this.#clearAllTargetSchedules();
        return;
      }

      const tableContainers = findAll("[data-controller='f-table-component']");

      tableContainers.forEach(tableContainer => {
        const desktopTable = find('#desktop-table-lines-filter', tableContainer);
        const mobileTable = find('#mobile-table-lines-filter', tableContainer);

        this.#clearTargetSchedules(desktopTable, mobileTable);
        this.#calculateDiffFor(desktopTable, mobileTable);
      });
    }

    #calculateDiffOnTableLoading(event) {
      this.#calculateDiffFor(event.detail.desktopTable, event.detail.mobileTable);
    }

    #calculateDiffFor(desktopTable, mobileTable) {
      if (desktopTable.id != 'desktop-table-lines-filter') return;
      if (this.#withoutTimeFilled()) return;

      const desktopCells = this.#desktopCells(desktopTable);
      const mobileCells = this.#mobileCells(mobileTable);

      if (isBlank(desktopCells)) return;

      desktopCells.forEach((desktopCell, position) => {
        const officialSchedule = desktopCell.officialScheduleCell.innerHTML;
        const shiftDelta = this.#calculateShiftDelta(officialSchedule);
        const officialScheduleDate = this.#dateForHour(officialSchedule);
        const calculatedScheduleDate = advanceDate(officialScheduleDate, shiftDelta, 'minutes');
        const calculatedSchedule = this.#hourFromDate(calculatedScheduleDate);

        [desktopCell.targetScheduleCell, mobileCells[position].targetScheduleCell]
          .forEach(cell => cell.innerHTML = this.#timeTargetOutput(calculatedSchedule, shiftDelta));
      });

      null;
    }

    #clearAllTargetSchedules() {
      findAll(`[id^='target-schedule']`).forEach(cell => cell.innerHTML = '-');
    }

    #clearTargetSchedules(...tables) {
      tables.forEach(table => { findAll(`[id^='target-schedule']`, table).forEach(cell => cell.innerHTML = '-') });
    }

    #cellsFor(table, device) {
      const officialSchedules = findAll(`[id^='official-schedule-${device}']`, table);
      const targetSchedules = findAll(`[id^='target-schedule-${device}']`, table);

      return officialSchedules.map(
        (schedule, position) => {
          return { officialScheduleCell: schedule, targetScheduleCell: targetSchedules[position] };
        }
      );
    }

    #desktopCells(table) {
      return this.#cellsFor(table, 'desktop');
    }

    #mobileCells(table) {
      return this.#cellsFor(table, 'mobile');
    }

    #timeShiftTypeOption() {
      return this.#currentTimeShiftTypeTarget().options[this.#currentTimeShiftTypeTarget().selectedIndex].value
    }

    #calculateShiftDelta(officialSchedule) {
      return isPresent(this.#currentFixedTimeTarget().value) ? this.#calculateShiftDeltaForFixedTime(officialSchedule) : this.#calculateShiftDeltaForFixedDelta();
    }

    #calculateShiftDeltaForFixedTime(officialSchedule) {
      const fixedTime = this.#currentFixedTimeTarget().value;

      const officialScheduleDate = this.#dateForHour(officialSchedule);
      const fixedScheduleDate = this.#dateForHour(fixedTime);
      const calculatedShiftDelta = hoursToMinutes(
        min(
          [
            HourDifference.orderedBetween(officialSchedule, fixedTime),
            HourDifference.orderedBetween(fixedTime, officialSchedule)
          ]
        )
      );

      return [1, -1]
        .map(signal => calculatedShiftDelta * signal)
        .find(deltaWithSignal => {
          return [advanceDate(fixedScheduleDate, -1, 'days'), fixedScheduleDate, advanceDate(fixedScheduleDate, 1, 'days')].some(schedule => {
            return advanceDate(officialScheduleDate, deltaWithSignal, 'minutes').getTime() == schedule.getTime();
          });
        });
    }

    #calculateShiftDeltaForFixedDelta() {
      const shiftDelta = Number.parseInt(this.#currentShiftDeltaTarget().value);

      return (this.#timeShiftTypeOption() === 'anticipated_time') ? shiftDelta * -1 : shiftDelta;
    }

    #dateForHour(hour) {
      const [hours, minutes] = hour.split(':');
      let date = new Date();

      date.setHours(Number.parseInt(hours));
      date.setMinutes(Number.parseInt(minutes));

      return date;
    }

    #hourFromDate(date) {
      return [
        rJust(date.getHours().toString(), 2, '0'),
        rJust(date.getMinutes().toString(), 2, '0'),
      ].join(':');
    }

    #timeTargetOutput(hour, shiftDelta) {
      const signal = (shiftDelta < 0) ? '-' : '+';

      return `${hour} (${signal} ${Math.abs(Math.round(shiftDelta))} min)`
    }

    #withoutTimeFilled() {
      return [this.#currentFixedTimeTarget(), this.#currentShiftDeltaTarget()]
        .filter(isPresent)
        .map(input => input.value)
        .every(isBlank);
    }

    #currentFixedTimeTarget() {
      return this.fixedTimeTargets.filter((fixedTimeTarget) => fixedTimeTarget.closest('fieldset:not([disabled])')).pop() || this.fixedTimeTarget;
    }

    #currentShiftDeltaTarget() {
      return this.shiftDeltaTargets.filter((shiftDeltaTarget) => shiftDeltaTarget.closest('fieldset:not([disabled])')).pop() || this.shiftDeltaTarget;
    }

    #currentTimeShiftTypeTarget() {
      return this.timeShiftTypeTargets.filter((timeShiftTypeTarget) => timeShiftTypeTarget.closest('fieldset:not([disabled])')).pop() || this.timeShiftTypeTarget;
    }
  }
);
